import { defineComponent as _defineComponent } from 'vue'
import axios from 'axios'
import {
  computed,
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  ref
} from 'vue'
import { useEventListener } from '@vueuse/core'
import { useAuth } from '@/composables/useAuth'
import { AUTH_STATE_CHANGED, emitter } from '@/plugins/emitter'
import PaymentForm from '@/components/Payment/PaymentMethodSelector.vue'
import CreateFormatDialog from '@/components/CreateFormatDialog.vue'
import { useReferralStatus } from '@/composables/yagiprime/useReferralStatus'
import {
  fillTemplate,
  formatFreeUntilLabel
} from '@/utils/yagiprime/referralCampaign'
import campaign from '@/utils/yagiprime/referralCampaign.json'
import { writeRouteCookie } from '@/utils/yagiprime/routeCookie'

type HostVm = {
  $deviceLib: { isMobileDevice: boolean }
  $varuna: { endPoints: Record<string, string> }
  $message: (opts: Record<string, unknown>) => void
  $cookies: Parameters<typeof writeRouteCookie>[0]
}

type CreateFormatDialogExpose = {
  openDialog: () => void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'YagiprimePaymentDialog',
  props: {
  /**
   * 加入時に無料になる日数。0 なら通常の価格表示。
   * 紹介キャンペーンのように無料期間が付く導線だけが値を渡す。
   * 実際に trial_end へ乗るのはサーバー側の計算なので、ここは表示専用。
   */
  freeDays: {
    type: Number,
    required: false,
    default: 0
  }
},
  setup(__props) {

const props = __props;

/**
 * ヤギプライム課金ダイアログ（vue-final-modal）。
 * open は mitt（show/open-yagiprime-payment-dialog）。hide は既存 mitt。
 */
const OPEN_YAGIPRIME_PAYMENT_DIALOG = 'open-yagiprime-payment-dialog'
const SHOW_YAGIPRIME_PAYMENT_DIALOG = 'show-yagiprime-payment-dialog'
const HIDE_YAGIPRIME_PAYMENT_DIALOG = 'hide-yagiprime-payment-dialog'
const PAYMENT_YAGIPRIME_RESULT = 'payment_yagiprime_result'
const YAGIPRIME_PAYMENT_SUCCESS = 'yagiprime-payment-success'



const vm = getCurrentInstance()!.proxy as unknown as HostVm
const { user, getClient } = useAuth()

const visible = ref(false)
const permissionType = ref(1)
const fullscreenLoading = ref(false)
const serviceName = ref('ヤギプライム')
const price = ref(550)
const dates = ref(30)
const selectedIndex = ref(1)
const hasRequiredFormat = ref(true)
const notificationSetting = ref('on')
const createFormatDialog = ref<CreateFormatDialogExpose | null>(null)

// 紹介キャンペーンの無料適用。日数の正本はサーバーで、ここは申込前の見込み表示
const {
  refreshStatus,
  clickCount,
  signupCount,
  successCount,
  pendingDaysTotal,
  isReferee
} = useReferralStatus()

/**
 * 実際に適用される無料日数。呼び出し側が渡していれば props を優先し
 * （被紹介者バナーはその値で開く）、無ければ status API の合計を使う。
 */
const effectiveFreeDays = computed(() =>
  props.freeDays > 0 ? props.freeDays : pendingDaysTotal.value
)

/** 期間が終わったら注記ごと消す（JSON の isActive が正本） */
const isCampaignActive = campaign.isActive

/**
 * 注記を出すか。被紹介者は紹介する側になれないので、無料日数が付かないなら
 * 書くことが何も無い（紹介者向けの「成立するとどうなるか」は当てはまらない）。
 */
const showReferralNotice = computed(
  () => isCampaignActive && (!isReferee.value || effectiveFreeDays.value > 0)
)

/**
 * 価格枠に出す注記。リンク（解約ページ）の前後で分けて組み立てる。
 * まだ無料適用が無い人にも件数 0 のまま出すが、本文は「成立するとどうなるか」の
 * 文に差し替える（無料期限が無いのに日付を書かないため）。
 *
 * 被紹介者には件数・成立上限を含まない bodyReferee を出す。本人は紹介する側に
 * なれないため「0クリック、0紹介成立のため無料」という因果のねじれた文になり、
 * 「マイページで無料期間の更新を確認できる」も当てはまらない。
 */
const referralNotice = computed(() => {
  const notice = campaign.paymentNotice
  const body = isReferee.value
    ? notice.bodyReferee
    : effectiveFreeDays.value > 0
      ? notice.body
      : notice.bodyNoFreeDays
  const filled = fillTemplate(body, {
    click: clickCount.value ?? 0,
    signup: signupCount.value ?? 0,
    success: successCount.value,
    until: formatFreeUntilLabel(effectiveFreeDays.value),
    limit: campaign.offer.maxReferralCount,
    daysPerReferral: campaign.offer.freeDaysPerReferral,
    price: campaign.offer.monthlyPriceTaxIncludedYen,
    cycle: campaign.offer.billingCycleDays
  })
  const [before, after] = filled.split('{cancel}')
  return {
    heading: notice.heading,
    before,
    after: after ?? '',
    cancelLabel: notice.cancelLabel,
    cancelPath: notice.cancelPath
  }
})

function currentUid(): string {
  return getClient()?.user?.uid ?? user.value?.uid ?? ''
}

function currentEmail(): string {
  return getClient()?.user?.email ?? user.value?.email ?? ''
}

function currentEmailVerified(): boolean {
  const u = getClient()?.user ?? user.value
  return !!(u && 'emailVerified' in u && u.emailVerified)
}

function onVisibleInput(value: boolean) {
  visible.value = value
}

// 引数は開いた機能名（加入導線）。無引数のときは呼び出し元（handleRouteId 等）が記録済み
function openPaymentDialog(feature?: unknown) {
  if (typeof feature === 'string' && feature) {
    writeRouteCookie(vm.$cookies, feature)
  }
  visible.value = true
  // 開くたびに取り直す。紹介が成立して無料日数が増えていても、キャッシュの
  // ままだと古い「◯日間無料」で課金条件を案内することになる
  refreshStatus()
  emitter.emit('change-loading-text', 'ヤギプライム登録中...')
  selectCard(1)
}

function closePaymentDialog() {
  visible.value = false
}

function selectCard(index: number) {
  selectedIndex.value = index

  if (index === 1) {
    price.value = 550
    dates.value = 30
  } else if (index === 2) {
    price.value = 1350
    dates.value = 90
  } else if (index === 3) {
    price.value = 550
    dates.value = 30
  }

  if (index !== 3) return

  try {
    const formData = new FormData()
    formData.append('uuid', currentUid())
    formData.append('version', '2')
    formData.append('reqflg', '2')
    formData.append('email', currentEmail())
    formData.append('site_flag', 'r')
    formData.append('device_flag', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('email_verified', currentEmailVerified() ? '1' : '0')

    axios
      .post(vm.$varuna.endPoints.getMypageHistory, formData, {
        timeout: 60000
      })
      .then((response) => {
        const history = response.data.format_ids as Array<{ format_id: string }>
        const requiredFormats = [
          'rirekisho_job_new',
          'rirekisho_job_new_single',
          'internship',
          'entrysheet'
        ]
        const formatExists = requiredFormats.some((format) =>
          history.some((item) => item.format_id === format)
        )
        if (!formatExists) {
          hasRequiredFormat.value = false
          createFormatDialog.value?.openDialog()
        }
      })
      .catch((error) => {
        console.error('フォーマットチェックに失敗しました:', error)
      })
  } catch (error) {
    console.error('フォーマットチェックに失敗しました:', error)
  }
}

async function authStateChanged() {
  const uid = currentUid()
  if (!uid) return
  try {
    const response = await axios.get(
      vm.$varuna.endPoints.isSubscribed + uid + '/',
      { timeout: 60000 }
    )
    if (response.data.is_subscribed) {
      visible.value = false
      emitter.emit(YAGIPRIME_PAYMENT_SUCCESS)
    }
  } catch {
    // ignore
  } finally {
    fullscreenLoading.value = false
  }
}

function onPaymentYagiprimeResult(result: unknown) {
  fullscreenLoading.value = false
  if (
    result === 'error' ||
    (result &&
      typeof result === 'object' &&
      (result as { status?: string }).status === 'error')
  ) {
    let errorMessage = '決済失敗しました'
    if (
      result &&
      typeof result === 'object' &&
      (result as { message?: string }).message
    ) {
      errorMessage = (result as { message: string }).message
    }
    vm.$message({
      message: errorMessage,
      type: 'error'
    })
  } else if (
    result === 'success' ||
    (result &&
      typeof result === 'object' &&
      (result as { status?: string }).status === 'success')
  ) {
    vm.$message({
      message: '決済完了しました',
      type: 'success'
    })
  }
}

function onLoadingOff() {
  fullscreenLoading.value = false
}

useEventListener(window, 'popstate', closePaymentDialog)

onBeforeMount(() => {
  emitter.on(OPEN_YAGIPRIME_PAYMENT_DIALOG, openPaymentDialog)
  emitter.on(SHOW_YAGIPRIME_PAYMENT_DIALOG, openPaymentDialog)
  emitter.on(HIDE_YAGIPRIME_PAYMENT_DIALOG, closePaymentDialog)
  emitter.on(AUTH_STATE_CHANGED, authStateChanged)
  emitter.on(PAYMENT_YAGIPRIME_RESULT, onPaymentYagiprimeResult)
  emitter.on('loading_off', onLoadingOff)
})

onBeforeUnmount(() => {
  emitter.off(OPEN_YAGIPRIME_PAYMENT_DIALOG, openPaymentDialog)
  emitter.off(SHOW_YAGIPRIME_PAYMENT_DIALOG, openPaymentDialog)
  emitter.off(HIDE_YAGIPRIME_PAYMENT_DIALOG, closePaymentDialog)
  emitter.off(AUTH_STATE_CHANGED, authStateChanged)
  emitter.off(PAYMENT_YAGIPRIME_RESULT, onPaymentYagiprimeResult)
  emitter.off('loading_off', onLoadingOff)
})

return { __sfc: true,OPEN_YAGIPRIME_PAYMENT_DIALOG, SHOW_YAGIPRIME_PAYMENT_DIALOG, HIDE_YAGIPRIME_PAYMENT_DIALOG, PAYMENT_YAGIPRIME_RESULT, YAGIPRIME_PAYMENT_SUCCESS, props, vm, user, getClient, visible, permissionType, fullscreenLoading, serviceName, price, dates, selectedIndex, hasRequiredFormat, notificationSetting, createFormatDialog, refreshStatus, clickCount, signupCount, successCount, pendingDaysTotal, isReferee, effectiveFreeDays, isCampaignActive, showReferralNotice, referralNotice, currentUid, currentEmail, currentEmailVerified, onVisibleInput, openPaymentDialog, closePaymentDialog, selectCard, authStateChanged, onPaymentYagiprimeResult, onLoadingOff, PaymentForm, CreateFormatDialog }
}

})